import {AppUrls} from 'shared/config/model/urls';

import React from 'react';
import {generatePath, Link} from 'react-router-dom';
import cn from 'classnames';
import {isEmpty} from 'lodash';
import {t} from '@lingui/core/macro';

import useDomainIncident from 'features/domain/model/useDomainIncident';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import CopyButton from 'shared/ui/IconButton/CopyButton/CopyButton';
import ArrowInTable from 'shared/ui/icons/ArrowInTable';
import OpenURL from 'shared/ui/icons/General/OpenURL';
import IDomainIncident from 'features/domain/model/DomainIncident/IDomainIncident';
import {translateDomainIncidentCause} from 'features/domain/lib/domainIncidentHelper';

import styles from './DomainIncident.module.scss';

interface IDomainIncidentProps {
    item: IDomainIncident,
}

const DomainIncident: React.FC<IDomainIncidentProps> = ({item}) => {
    const linkColorClass = styles.colorLink;

    const {
        isRobotsDiff,
        diffData,
        isLoadingDiff,
        isSaving,
        isSaved,
        domainMonitorId,
        openRobotsTxt: handleOpenRobotsTxt,
        saveExpectedRobots: handleSaveExpected,
    } = useDomainIncident(item);

    return (
        <>
            <ModalBodyWrapper>
                <div className={styles.container}>
                    <span className={styles.title}>{t`Причина`}:</span>
                    <span className={styles.textError}>
                        {translateDomainIncidentCause(item.type, item.error_message)}
                    </span>
                </div>
            </ModalBodyWrapper>
            {
                isRobotsDiff &&
                    <ModalBodyWrapper>
                        <div className={styles.container}>
                            <span className={styles.titleBlock}>
                                <span className={styles.title}>{t`Сравнение`}</span>
                                <a
                                    className={cn(styles.link, linkColorClass)}
                                    onClick={handleOpenRobotsTxt}
                                >
                                    <span>{t`Открыть robots.txt`}</span>
                                    <OpenURL/>
                                </a>
                            </span>
                            <div className={styles.answersBlock}>
                                <div className={styles.titleBlock}>
                                    <span className={styles.title}>{t`Содержимое robots.txt`}</span>
                                    <CopyButton value={item.robots_txt_body || ''}/>
                                </div>
                                <div className={styles.bodyBlock}>
                                    {item?.robotsTxtBodyHumanReadable?.map((line, index) => (
                                        <div key={index} className={styles.text}>
                                            {line}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.answersBlock}>
                                <div className={styles.titleBlock}>
                                    <span className={styles.title}>{t`Отличия от эталона`}</span>
                                    <CopyButton
                                        value={diffData?.diff?.map(line => line.line).join('\n') ?? ''}
                                        disabled={!diffData}
                                    />
                                </div>
                                <div className={styles.bodyBlock}>
                                    {diffData?.diff?.map((line, index) => (
                                        <div key={index}>
                                            {
                                                line.type === '-' &&
                                                <div className={styles.errorLine}>
                                                    - {line.line}
                                                </div>
                                            }
                                            {
                                                line.type === '+' &&
                                                <div className={styles.successLine}>
                                                    + {line.line}
                                                </div>
                                            }
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {
                                !item?.resolved &&
                                    <button
                                        onClick={handleSaveExpected}
                                        disabled={isSaving || isLoadingDiff}
                                        className={styles.button}
                                        type="button"
                                    >
                                        {isSaving ? t`Сохранение...` : t`Принять отличия как эталон`}
                                    </button>
                            }
                            {
                                isSaved &&
                                <div className={styles.successMessage}>
                                    {t`Эталон успешно сохранен!`}
                                </div>
                            }
                        </div>
                    </ModalBodyWrapper>
            }

            {
                domainMonitorId &&
                    <ModalBodyWrapper>
                        <div className={styles.container}>
                            <div className={styles.title}>{t`Монитор домена`}</div>
                            <span className={styles.line}/>
                            <div className={styles.monitorBlock}>
                                <Link
                                    to={generatePath(AppUrls.DOMAIN_MONITOR_DETAILS, {domainMonitorId})}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={cn(styles.monitorLink, linkColorClass)}
                                >
                                    {item.domainHumanReadable || item.domain}
                                    <ArrowInTable/>
                                </Link>
                            </div>
                        </div>
                    </ModalBodyWrapper>
            }

            {
                item.monitors
                && !isEmpty(item.monitors)
                && <ModalBodyWrapper>
                    <div className={styles.container}>
                        <div className={styles.title}>{t`Мониторы сайтов`}</div>
                        <span className={styles.line}/>
                        <div className={styles.monitorBlock}>
                            {item.monitors.map((monitor, index) => (
                                <div key={monitor.uuid}>
                                    <Link
                                        to={generatePath(AppUrls.HTTP_MONITOR_DETAILS, {monitorId: monitor.uuid})}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={cn(styles.monitorLink, linkColorClass)}
                                    >
                                        {monitor.name}
                                        <ArrowInTable/>
                                    </Link>
                                    {index < item.monitors.length - 1 && (
                                        <span className={styles.line}/>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </ModalBodyWrapper>
            }
        </>
    );
};

export default DomainIncident;
